import { AnnouncementPersonalQueue, ManualNotificationQueuesStatusType, PermissionType } from '@golden/gdk-admin'
import { Link } from 'react-router-dom'
import Grid from '@material-ui/core/Grid'
import Box from '@material-ui/core/Box'
import Paper from '@material-ui/core/Paper'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import useT from '../../../i18ns/admin/useT'
import { useChecker } from '../../../utils/admin/AdminRouteHook'
import React, { useMemo, useState, useEffect } from 'react'
import { useDialogHandleClick, useGetData, useReload } from '../../../utils/default/ComplexFlowHook'
import useGDK from '../../../providers/admin/gdk/useGDK'
import { usePageFlow } from '../../../utils/default/PageFlowHook'
import DateTime from '../../default/present/DateTime'
import manualNotificationQueuesStatusName from '../../../constants/admin/manualNotificationQueuesStatusName'
import manualNotificationQueuesStatusColor from '../../../constants/admin/manualNotificationQueuesStatusColor'
import Button from '@material-ui/core/Button'
import { createTableData } from '../../../utils/default/TableHelper'
import LoadingAndErrorFrame from '../../default/frames/LoadingAndErrorFrame'
import CoreTable from '../../default/present/CoreTable'
import useGlobalDialog from '../../../providers/admin/dialog/useGlobalDialog'
import { createGlobalDialogConfig } from '../../../utils/default/DialogHelper'
import allRoute from '../route/route'
import { timer } from 'rxjs'

export interface RowType {
  id: number
  order: number | '-'
  title: string
  count: number
  updateBy: string
  updateAt: React.ReactElement
  status: React.ReactElement
  operation: React.ReactElement
}

const DeleteButton: React.FC<{ id: number, reload: () => void }> = (props) => {
  const { id, reload } = props
  const { t } = useT()
  const commonClasses = useCommonStyles()
  const gdk = useGDK()
  const globalDialog = useGlobalDialog()

  const handleDelete = useDialogHandleClick({
    dialogId: `deleteAnnouncementPersonalQueue-${id}`,
    globalDialog,
    changeDialogConfig: createGlobalDialogConfig({
      showIcon: false,
      message: t('dialog.confirmDelete')
    }),
    successDialogConfig: createGlobalDialogConfig({
      showIcon: false,
      message: t('dialog.deleteFinish'),
      showCancel: false
    }),
    getFailDialogConfig: (error: string) => createGlobalDialogConfig({
      variant: 'error',
      showIcon: true,
      message: error,
      showCancel: false
    }),
    payload: id,
    gdkFunc: (payload) => gdk.notification.deletePersonalNotificationQueues(payload),
    gdkFuncDependencies: [gdk],
    onSuccess: () => { reload() },
    onSuccessDependencies: [reload]
  })
  return (
    <Button
      size="small"
      className={commonClasses.blueGradualOutlineButton}
      onClick={handleDelete}
    >
      {t('common.delete')}
    </Button>
  )
}

const AnnouncementPersonalProcessTable: React.FC = () => {
  const { t } = useT()
  const gdk = useGDK()
  const writableResend = useChecker([PermissionType.SEND_MESSAGE])
  const writableTemplate = useChecker([PermissionType.SEND_MESSAGE_TEMPLATE])
  const pageFlow = usePageFlow()
  const commonClasses = useCommonStyles()
  const [list, setList] = useState<Array<AnnouncementPersonalQueue<Date>>>([])
  const { reload, reloadFlag } = useReload()

  const tableClasses = useMemo(() => ({
    head: commonClasses.pinkTableHead,
    row: commonClasses.tableRow,
    cellHead: commonClasses.tableCellHead,
    cellBody: commonClasses.pre
  }), [commonClasses])

  useGetData({
    gdkFunc: () => gdk.notification.getPersonalNotificationQueues(),
    gdkFuncDependencies: [reloadFlag],
    onBeforeFetch: pageFlow.setLoadingStart,
    onSuccess: (res: Array<AnnouncementPersonalQueue<Date>>) => {
      setList(res)
      pageFlow.setContentShow()
    },
    onError: pageFlow.setGDKError
  })

  const rows: RowType[] = useMemo(() => list.map((item) => {
    return {
      id: item.id,
      order: item.order ?? '--',
      title: item.title,
      count: item.count,
      updateBy: item.updated_by,
      updateAt: (<DateTime time={item.updated_at} />),
      status: (
        <span className={commonClasses.chipText} style={{ backgroundColor: manualNotificationQueuesStatusColor[item.status] }}>
          { t(manualNotificationQueuesStatusName[item.status]) }
        </span>
      ),
      operation: item.status === ManualNotificationQueuesStatusType.FAILED
        ? (<DeleteButton id={item.id} reload={reload} />)
        : '-'
    } as RowType
  }), [commonClasses.chipText, list, reload, t])

  const data = useMemo(() => {
    return createTableData<RowType>(
      {
        id: {
          label: '',
          value: 'id'
        },
        order: {
          label: t('common.queueOrder'),
          value: 'order',
          align: 'center'
        },
        title: {
          label: `${t('common.title')}/${t('common.playerAccount')}`,
          value: 'title',
          align: 'center'
        },
        count: {
          label: t('common.amount'),
          value: 'count',
          align: 'center'
        },
        updateBy: {
          label: t('common.updateBy'),
          value: 'updateBy',
          align: 'center'
        },
        updateAt: {
          label: t('common.updateAt'),
          value: 'updateAt',
          align: 'center'
        },
        status: {
          label: t('common.process'),
          value: 'status',
          align: 'center'
        },
        operation: {
          label: t('common.operation'),
          value: 'operation',
          align: 'center'
        }
      },
      [
        'order',
        'title',
        'count',
        'updateBy',
        'updateAt',
        'status',
        ...(writableResend ? ['operation'] as const : [])
      ],
      rows,
      'id'
    )
  }, [rows, t, writableResend])

  const queuesCount = useMemo(() => list.reduce((accu, curr) => {
    if (curr.status === ManualNotificationQueuesStatusType.FAILED) {
      return { count: accu.count, failed: accu.failed + 1 }
    }

    return { count: accu.count + 1, failed: accu.failed }
  }, { count: 0, failed: 0 }), [list])

  useEffect(() => {
    const subscription = timer(0, 10000).subscribe(() => { reload() })
    return () => { subscription.unsubscribe() }
  }, [reload])

  return (
    <React.Fragment>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Box display="flex" flexDirection="row" alignItems="center">
            {`${t('common.currentQueueWithColon')}${queuesCount.count}`}
            { queuesCount.failed > 0 && (<span style={{ marginLeft: 20 }}>{`${t('common.failedWithColon')}${queuesCount.failed}`}</span>)}
          </Box>
        </Grid>
        {(writableResend || writableTemplate) && (
          <Grid item>
            <Grid container spacing={2}>
              { writableTemplate && (<Grid item>
                <Button
                  classes={{ disabled: commonClasses.disabledButton }}
                  className={commonClasses.purpleGradualOutlineButton}
                  component={Link}
                  to={allRoute.forestagePersonalTemplateManagement.encodePath({ search: {}, param: {} })}
                >
                  {t('common.templateManagement')}
                </Button>
              </Grid>) }
              { writableResend && (<Grid item>
                <Button
                  classes={{ disabled: commonClasses.disabledButton }}
                  className={commonClasses.purpleGradualButton}
                  component={Link}
                  to={allRoute.forestagePersonalTemplate.encodePath({ search: {}, param: {} })}
                >
                  {t('common.sendMessage')}
                </Button>
              </Grid>) }
            </Grid>
          </Grid>
        )}
      </Grid>
      {
        list.length > 0 && (
          <LoadingAndErrorFrame { ...pageFlow.status }>

            <Paper>
              <Box padding={4} marginTop={2}>
                <CoreTable
                  classes={tableClasses}
                  data={data}
                  total={list.length}
                  showNoData={false}
                />
              </Box>
            </Paper>
          </LoadingAndErrorFrame>
        )
      }
    </React.Fragment>
  )
}

export default React.memo(AnnouncementPersonalProcessTable)
